import * as React from "react";
import DashboardWrapper from "../../components/DashboardWrapper";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
} from "@mui/material";
import Spinner from "../../components/Spinner";
import { showToast } from "../../store/slices/common";
import { useDispatch } from "react-redux";
import {
  downloadRetentionReport,
  getActiveUsersList,
  getRetentionUsersList,
  getActiveUsersMonthComparison,
} from "../../http/dashboard";
import { Select, DatePicker } from "antd";
import AboutTable from "../Dashboard/AboutTable";
import dayjs from "dayjs";

export default function UsersDashboard() {
  const [loading, setLoading] = React.useState(false);
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = React.useState("retention");
  const [filterValue, setFilterValue] = React.useState("last30days");
  const [page, setPage] = React.useState(0);
  const [listData, setListData] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);

  const [retained, setRetained] = React.useState(0);
  const [retentionTotal, setRetentionTotal] = React.useState(0);
  const [retentionRate, setRetentionRate] = React.useState(0);

  const [activeDateType, setActiveDateType] = React.useState("month");
  const [fromDate, setFromDate] = React.useState(null);
  const [toDate, setToDate] = React.useState(null);
  const [selectedMonth, setSelectedMonth] = React.useState(dayjs());

  // States for month comparison (active users only)
  const [fromMonthYear, setFromMonthYear] = React.useState(dayjs());
  const [toMonthYear, setToMonthYear] = React.useState(dayjs());
  const [monthComparisonData, setMonthComparisonData] = React.useState(null);

  React.useEffect(() => {
    if (activeTab === "retention") {
      fetchRetentionUsers();
    } else {
      fetchActiveUsers();
    }
  }, [
    activeTab,
    filterValue,
    page,
    activeDateType,
    fromDate,
    toDate,
    selectedMonth,
    fromMonthYear,
    toMonthYear,
  ]);

  const fetchRetentionUsers = async () => {
    try {
      setLoading(true);
      const payload = { page: page + 1, limit: 10 };

      if (activeDateType === "range" && fromDate && toDate) {
        payload.fromDate = fromDate.format("YYYY-MM-DD");
        payload.toDate = toDate.format("YYYY-MM-DD");
      } else if (activeDateType === "single" && fromDate) {
        payload.fromDate = fromDate.format("YYYY-MM-DD");
        payload.toDate = fromDate.format("YYYY-MM-DD");
      } else if (activeDateType === "month" && selectedMonth) {
        payload.year = selectedMonth.year();
        payload.month = selectedMonth.month() + 1;
      }

      const res = await getRetentionUsersList(payload);
      const data = res?.data?.data;
      setListData(data?.report || []);
      setTotalCount(data?.totalRetainedUsers || 0);
      setRetained(data?.totalRetainedUsers || 0);
      setRetentionTotal(data?.totalRegisteredUsers || 0);
      setRetentionRate(data?.retentionRate);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(showToast({ color: "error", msg: "Failed to fetch retention users." }));
    }
  };

  const fetchActiveUsers = async () => {
    try {
      setLoading(true);

      if (activeDateType === "monthComparison") {
        const payload = {
          fromMonth: fromMonthYear.month() + 1,
          fromYear: fromMonthYear.year(),
          toMonth: toMonthYear.month() + 1,
          toYear: toMonthYear.year(),
        };

        const res = await getActiveUsersMonthComparison(payload);
        const data = res?.data?.data;
        setMonthComparisonData(data);
        setListData([]);
        setTotalCount(0);
      } else {
        const payload = { page: page + 1, limit: 10 };

        if (activeDateType === "range" && fromDate && toDate) {
          payload.fromDate = fromDate.format("YYYY-MM-DD");
          payload.toDate = toDate.format("YYYY-MM-DD");
        } else if (activeDateType === "single" && fromDate) {
          payload.fromDate = fromDate.format("YYYY-MM-DD");
          payload.toDate = fromDate.format("YYYY-MM-DD");
        } else if (activeDateType === "month" && selectedMonth) {
          payload.year = selectedMonth.year();
          payload.month = selectedMonth.month() + 1;
        }

        const res = await getActiveUsersList(payload);
        const data = res?.data?.data;
        setListData(data?.report || []);
        setTotalCount(data?.totalUsers || 0);
        setMonthComparisonData(null);
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(showToast({ color: "error", msg: "Failed to fetch active users." }));
    }
  };

  const handleDownloadReport = async (budgetId) => {
    try {
      setLoading(true);
      const res = await downloadRetentionReport({
        budgetReportId: budgetId,
        lang: "en",
        currency: "CAD",
        surplusOrDeficit: "Surplus",
      });

      const byteArray = Object.values(res.data.data?.data?.buffer);
      const uint8Array = new Uint8Array(byteArray);
      const blob = new Blob([uint8Array], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", res.data.data?.data?.filename);
      document.body.appendChild(link);
      link.click();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(showToast({ color: "error", msg: "Failed to download report." }));
    }
  };

  return (
    <DashboardWrapper heading="Retention Reports">
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => {
          setActiveTab(newValue);
          setPage(0);
          setFilterValue(newValue === "retention" ? "last30days" : "MAU");
          if (newValue === "active") {
            setActiveDateType("month");
            setSelectedMonth(dayjs());
            setFromDate(null);
            setToDate(null);
            setMonthComparisonData(null);
          } else {
            setActiveDateType("month");
            setSelectedMonth(dayjs());
            setFromDate(null);
            setToDate(null);
          }
        }}
        sx={{
          mb: 3,
          ".MuiTabs-flexContainer": { gap: 2 },
          ".MuiTabs-indicator": { display: "none" },
        }}
      >
        <Tab
          label="Retained Users"
          value="retention"
          sx={{
            textTransform: "none",
            borderRadius: "4px",
            fontSize: "14px",
            fontWeight: 500,
            px: 2.5,
            py: 1,
            backgroundColor: activeTab === "retention" ? "#0832DE" : "transparent",
            color: activeTab === "retention" ? "#fff !important" : "#0832DE",
            border: "1px solid #0832DE",
          }}
        />
        <Tab
          label="Active Users"
          value="active"
          sx={{
            textTransform: "none",
            borderRadius: "4px",
            fontSize: "14px",
            fontWeight: 500,
            px: 2.5,
            py: 1,
            backgroundColor: activeTab === "active" ? "#0832DE" : "transparent",
            color: activeTab === "active" ? "#fff !important" : "#0832DE",
            border: "1px solid #0832DE",
          }}
        />
      </Tabs>
      {activeTab === "active" && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          Active Users include both new registrations and existing users who logged in at least once during the selected period.
        </Typography>
      )}
      {!loading ? (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              {activeTab === "retention" ? (
                <Box display="flex" gap={2} alignItems="center">
                  <Select
                    value={activeDateType}
                    style={{ width: 200 }}
                    options={[
                      { label: "Single Date", value: "single" },
                      { label: "Date Range", value: "range" },
                      { label: "Month", value: "month" },
                    ]}
                    onChange={(val) => {
                      setActiveDateType(val);
                      setPage(0);
                      setFromDate(null);
                      setToDate(null);
                      setSelectedMonth(val === "month" ? dayjs() : null);
                    }}
                  />
                  {activeDateType === "single" && (
                    <DatePicker
                      onChange={(date) => setFromDate(date)}
                      value={fromDate}
                      disabledDate={(current) =>
                        current && current > dayjs().endOf("day")
                      }
                    />
                  )}
                  {activeDateType === "range" && (
                    <DatePicker.RangePicker
                      onChange={(dates) => {
                        setFromDate(dates?.[0]);
                        setToDate(dates?.[1]);
                      }}
                      value={[fromDate, toDate]}
                      disabledDate={(current) =>
                        current && current > dayjs().endOf("day")
                      }
                    />
                  )}
                  {activeDateType === "month" && (
                    <DatePicker
                      picker="month"
                      onChange={(date) => setSelectedMonth(date)}
                      value={selectedMonth}
                      disabledDate={(current) =>
                        current && current > dayjs().endOf("month")
                      }
                    />
                  )}
                </Box>
              ) : (
                <Box display="flex" gap={2} alignItems="center">
                  <Select
                    value={activeDateType}
                    style={{ width: 200 }}
                    options={[
                      { label: "Single Date", value: "single" },
                      { label: "Date Range", value: "range" },
                      { label: "Month", value: "month" },
                      { label: "Month Comparison", value: "monthComparison" },
                    ]}
                    onChange={(val) => {
                      setActiveDateType(val);
                      setPage(0);
                      setFromDate(null);
                      setToDate(null);
                      setSelectedMonth(val === "month" ? dayjs() : null);
                      if (val === "monthComparison") {
                        setFromMonthYear(dayjs().subtract(1, "month"));
                        setToMonthYear(dayjs());
                      }
                      setMonthComparisonData(null);
                    }}
                  />
                  {activeDateType === "single" && (
                    <DatePicker
                      onChange={(date) => setFromDate(date)}
                      value={fromDate}
                      disabledDate={(current) =>
                        current && current > dayjs().endOf("day")
                      }
                    />
                  )}
                  {activeDateType === "range" && (
                    <DatePicker.RangePicker
                      onChange={(dates) => {
                        setFromDate(dates?.[0]);
                        setToDate(dates?.[1]);
                      }}
                      value={[fromDate, toDate]}
                      disabledDate={(current) =>
                        current && current > dayjs().endOf("day")
                      }
                    />
                  )}
                  {activeDateType === "month" && (
                    <DatePicker
                      picker="month"
                      onChange={(date) => setSelectedMonth(date)}
                      value={selectedMonth}
                      disabledDate={(current) =>
                        current && current > dayjs().endOf("month")
                      }
                    />
                  )}
                  {activeDateType === "monthComparison" && (
                    <>
                      <Typography sx={{ mr: 1 }}>First Month:</Typography>
                      <DatePicker
                        picker="month"
                        onChange={(date) => setFromMonthYear(date)}
                        value={fromMonthYear}
                        disabledDate={(current) => {
                          const now = dayjs();
                          const isFuture = current && current > now.endOf("month");
                          const isSameAsToMonth =
                            toMonthYear && current && current.isSame(toMonthYear, "month");
                          return isFuture || isSameAsToMonth;
                        }}
                      />

                      <Typography sx={{ mx: 1 }}>Second Month:</Typography>
                      <DatePicker
                        picker="month"
                        onChange={(date) => setToMonthYear(date)}
                        value={toMonthYear}
                        disabledDate={(current) => {
                          const now = dayjs();
                          const isFuture = current && current > now.endOf("month");
                          const isSameAsFromMonth =
                            fromMonthYear && current && current.isSame(fromMonthYear, "month");
                          return isFuture || isSameAsFromMonth;
                        }}
                      />
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {activeTab === "retention" && (
            <Box display="flex" justifyContent="flex-start" mt={2} mb={2}>
              <Card
                sx={{
                  width: "100%",
                  maxWidth: 400,
                  p: 2,
                  borderRadius: 4,
                  boxShadow: 3,
                  bgcolor: "#f9f9f9",
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Retention Summary
                  </Typography>

                  <Typography mb={1}>
                    <strong>Retained Users:</strong> {retained}
                    <br />
                    <Typography variant="body2" color="text.secondary">
                      Existing users who accessed their profile again during the selected period.
                    </Typography>
                  </Typography>

                  <Typography mb={1}>
                    <strong>Total Users:</strong> {retentionTotal}
                    <br />
                    <Typography variant="body2" color="text.secondary">
                      All users (both new and returning) who accessed the platform during the selected period.
                    </Typography>
                  </Typography>

                  <Typography mb={1}>
                    <strong>Retention Rate:</strong> {retentionRate}%
                    <br />
                    <Typography variant="body2" color="text.secondary">
                      Percentage of retained users out of total users.
                    </Typography>
                  </Typography>

                  <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Note:</strong> New Users = profiles created during the selected period. <br />
                      Returning Users = existing users who accessed their profile during the same period.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {activeTab === "active" && activeDateType === "monthComparison" && monthComparisonData && (
            <Box
              sx={{
                width: 350,
                p: 2,
                borderRadius: 4,
                boxShadow: 3,
                bgcolor: "#e6f0ff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                mt: 2,
              }}
            >
              <Typography variant="h6" sx={{ flexBasis: "100%" }}>
                Month Comparison
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  textAlign: "center",
                  borderRight: "1px solid #aac4ff",
                  pr: 2,
                }}
              >
                <Typography fontWeight="bold" fontSize={16} mb={1}>
                  {dayjs(`${monthComparisonData.fromYear}-${monthComparisonData.fromMonth}-01`).format(
                    "MMM YYYY"
                  )}
                </Typography>
                <Typography fontWeight="600" color="#0832DE" fontSize={24}>
                  {monthComparisonData.fromTrendCount ?? "N/A"}
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  Trend Count
                </Typography>
              </Box>

              <Box sx={{ flex: 1, textAlign: "center", pl: 2 }}>
                <Typography fontWeight="bold" fontSize={16} mb={1}>
                  {dayjs(`${monthComparisonData.toYear}-${monthComparisonData.toMonth}-01`).format(
                    "MMM YYYY"
                  )}
                </Typography>
                <Typography fontWeight="600" color="#0832DE" fontSize={24}>
                  {monthComparisonData.toTrendCount ?? "N/A"}
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  Trend Count
                </Typography>
              </Box>
            </Box>
          )}

          {!(activeTab === "active" && activeDateType === "monthComparison") && (
            <>
              {listData.length > 0 ? (
                <AboutTable
                  listData={listData}
                  handleChangePage={(evt, pageNum) => setPage(pageNum)}
                  page={page}
                  totalCount={totalCount}
                  showDownloadReport
                  handleDownloadReport={handleDownloadReport}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: 200,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: 18,
                    color: "#888",
                  }}
                >
                  No Data
                </Box>
              )}
            </>
          )}
        </>
      ) : (
        <Spinner />
      )}
    </DashboardWrapper>
    )
}